import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { CreditCard, LogOut, MapPin, Package, Receipt, Truck, User as UserIcon, Heart, Bell } from 'lucide-react';
import { useAuth, useSignOut } from '@kss/auth';

const NAV = [
  { to: '/account', label: 'Overview', icon: UserIcon, end: true },
  { to: '/account/orders', label: 'My orders', icon: Package },
  { to: '/account/track-order', label: 'Track order', icon: Truck },
  /* SSO ISSUE 4 — dedicated "My invoices" section so the customer can
     open the same print-ready layout the panel renders. */
  { to: '/account/invoices', label: 'My invoices', icon: Receipt },
  { to: '/account/profile', label: 'Profile', icon: UserIcon },
  { to: '/account/addresses', label: 'Addresses', icon: MapPin },
  { to: '/account/payment-methods', label: 'Payment methods', icon: CreditCard },
  { to: '/account/wishlist', label: 'Wishlist', icon: Heart },
  { to: '/account/notifications', label: 'Notifications', icon: Bell },
];

/**
 * Two-column shell for the customer-facing account dashboard. Left rail
 * holds the section nav; right side renders the active route via Outlet.
 *
 * Reuses `WebLayout` (the parent route) so cart drawer / mega menus / login
 * modal all stay available.
 */
export function AccountLayout() {
  const { user } = useAuth();
  const signOut = useSignOut();
  const navigate = useNavigate();

  async function handleSignOut() {
    await signOut();
    navigate('/', { replace: true });
  }

  return (
    <div className="mx-auto grid max-w-6xl gap-6 px-6 py-8 md:grid-cols-[240px_1fr]">
      <aside className="flex h-fit flex-col gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] p-3">
        <div className="px-2 pb-3">
          <p className="text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
            Signed in as
          </p>
          <p className="truncate text-sm font-semibold text-[var(--color-text-primary)]">
            {user?.name ?? 'Guest'}
          </p>
          {user?.email && (
            <p className="truncate text-xs text-[var(--color-text-secondary)]">{user.email}</p>
          )}
        </div>
        <nav className="grid">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                [
                  'flex items-center gap-2 rounded px-2 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-[var(--color-primary)] text-[var(--color-primary-fg)]'
                    : 'text-[var(--color-text-primary)] hover:bg-[var(--color-bg-card-hover)]',
                ].join(' ')
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-2 border-t border-[var(--color-border)] pt-2">
          <button
            type="button"
            onClick={handleSignOut}
            className="flex w-full items-center gap-2 rounded px-2 py-2 text-sm text-[var(--color-danger)] transition-colors hover:bg-[var(--color-bg-card-hover)]"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>
      <main className="min-w-0">
        <Outlet />
      </main>
    </div>
  );
}

export default AccountLayout;
